import { ReactNode, useEffect, useRef, useState } from "react";
import { useGetCompanyOrdersQuery } from "../../../../../../API/authApi";
import { CURRENCY_SYMBOLS } from "../../../../../../Costants/costants";
import { useGeneralHooks } from "../../../../../../Hooks/hooks";
import { IMyBusinessPageNav, MyBusinessContentItems } from "../../../../../../Interfaces/componentTypes";
import { IMyBusinessProps } from "../../../../../My_Business/myBusiness-hooks";
import { Building, Dollar, MenuList, QrCode, Service, Orders } from "../../../../../../Assets/Icons";
import MyMenu from "../../../Components/Menu/MyMenu/myMenu";
import Finances from "../../../Components/Finances/finances";
import Staff from "../../../Components/Staff/staff";
import Qr from "../../../Components/QR/qr";
import Services from "../../../Components/Services/services";
import OrdersPage from "../../../Components/Orders/orders";
import { getSWRegistration, showLocalNotification } from "../../../../../../Utilits/utilits";
import BusinessProfile from "../../../Components/BusinessProfile/businessProfile";
import useDialogHooks from "../../../../../../Components/Dialog/dialog-hooks";
import PushSubscribeDialog from "../../../Components/Dialogs/PushSubscribe/pushSubscribeDialog";

const useMainPageHooks = (props: IMyBusinessProps) => {
  const { data, onCompanyUpdate } = props;
  const services = data?.services;
  const id = data.id;
  const isAnyServiceEnabled =
    services && (services.delivery.is_active || services.pre_order || services.reserve);

  const { t, largeScreen } = useGeneralHooks();
  const navRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { open, onOpen, onClose } = useDialogHooks();
  const [dialogComponent, setDialogComponent] = useState<ReactNode | null>(null);
  const [activeContent, setActiveContent] = useState<MyBusinessContentItems>(
    isAnyServiceEnabled ? MyBusinessContentItems.ORDERS : MyBusinessContentItems.MY_MENU
  );
  const [navHeight, setNavHeight] = useState(0);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  const { data: orders, refetch } = useGetCompanyOrdersQuery(
    { id },
    { 
      pollingInterval: isAnyServiceEnabled && isTabVisible ? 15000 : 0, 
      skip: !id 
    }
  );

  const newOrdersCount = orders?.new_orders?.length ?? 0;
  const currencySymbol = data.cost_currency
    ? CURRENCY_SYMBOLS[data.cost_currency]
    : CURRENCY_SYMBOLS.amd;

  // ---------------- Audio (Web Audio API) ----------------
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const loopSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioUnlocked = useRef(false);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  // Load sound file once
  useEffect(() => {
    const loadAudio = async () => {
      try {
        const AC = window.AudioContext || (window as any).webkitAudioContext;
        if (!AC) {
          console.warn("Web Audio API not supported.");
          return;
        }
        const ctx = new AC();
        audioContextRef.current = ctx;

        const res = await fetch("/Sounds/notify.mp3");
        const arrayBuffer = await res.arrayBuffer();
        const decoded = await new Promise<AudioBuffer>((resolve, reject) => {
          ctx.decodeAudioData(arrayBuffer, resolve, reject);
        });

        audioBufferRef.current = decoded;
      } catch (err) {
        console.error("Failed to load sound:", err);
      }
    };

    loadAudio();
  }, []);

  const onCloseDialog = () => {
    setDialogComponent(null);
    onClose()
  };

  useEffect(() => {
    const subEnabled = localStorage.getItem(`sub_${data.id}`);
    const checkSubscription = async () => {
      if (!isAnyServiceEnabled) return;
      try {
        // const registration = await navigator.serviceWorker.ready;
        // const subscription = await registration.pushManager.getSubscription();

        if (!subEnabled) {
          setDialogComponent(
            <PushSubscribeDialog
              companyId={id}
              onClose={onCloseDialog}
            />
          );
          onOpen(); 
        }
      } catch (err) {
        console.error("Ошибка при проверке push подписки:", err);
      }
    };

    checkSubscription();
  }, [isAnyServiceEnabled, id]);

  const startLoopingSound = () => {
    const ctx = audioContextRef.current;
    const buf = audioBufferRef.current;
    if (!ctx || !buf || !audioUnlocked.current) return;
    if (loopSourceRef.current) return; 

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(ctx.destination);
    src.start(0);
    loopSourceRef.current = src;
  };

  const stopLoopingSound = () => {
    const src = loopSourceRef.current;
    if (!src) return;
    try {
      src.stop();
      src.disconnect();
    } catch { }
    loopSourceRef.current = null;
  };

  useEffect(() => {
    const notifySW = async () => {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") {
        await Notification.requestPermission();
      }

      // Always send notification through service worker when new orders arrive
      // This ensures custom sound plays even when tab is hidden!
      if (newOrdersCount > 0) {
        const swReg = await getSWRegistration();
        if (swReg && swReg.active) {
          // Use postMessage to trigger service worker's NEW_ORDERS handler
          // This handler includes the custom sound property
          swReg.active.postMessage({
            type: "NEW_ORDERS",
            count: newOrdersCount,
            companyName: data.name || "New Order"
          });
        } else if (swReg) {
          // Fallback: use showLocalNotification if active worker not available
          showLocalNotification(
            `📦 ${newOrdersCount} new order(s)!`,
            "Open your dashboard to manage orders.",
            swReg
          );
        }
      }
    };

    if (newOrdersCount > 0) {
      startLoopingSound();
      notifySW();
    } else {
      stopLoopingSound();
    }
  }, [newOrdersCount, data.name]);

  useEffect(() => {
    document.title =
      newOrdersCount > 0 ? `(${newOrdersCount}) New Orders!` : "My Business Dashboard";
  }, [newOrdersCount]);

  // ---------------- Navigation ----------------
  useEffect(() => {
    const currentUrl = window.location.href.split("?")[0];
    window.history.pushState({}, "", `${currentUrl}?tab=${isAnyServiceEnabled ? MyBusinessContentItems.ORDERS : MyBusinessContentItems.MY_MENU}`);
  }, []);

  useEffect(() => {
    if (navRef.current) setNavHeight(navRef.current.offsetHeight);
  }, [largeScreen]);

  // ---------------- Tab Visibility Detection ----------------
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasHidden = !isTabVisible;
      const isNowVisible = !document.hidden;
      
      setIsTabVisible(isNowVisible);
      
      // If tab becomes visible and services are enabled, fetch immediately
      if (wasHidden && isNowVisible && isAnyServiceEnabled && id) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTabVisible, isAnyServiceEnabled, id, refetch]);

  const handleNavClick = (index: number, id: MyBusinessContentItems) => {
    setActiveContent(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const currentUrl = window.location.href.split("?")[0];
    window.history.pushState({}, "", `${currentUrl}?tab=${id}`);

    if (navItemRefs.current[index] && navRef.current && !largeScreen) {
      navItemRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  };

  const BUSINESS_PAGE_NAVS: IMyBusinessPageNav[] = [
    ...(isAnyServiceEnabled
      ? [{ title: t("My_Business.Orders"), id: MyBusinessContentItems.ORDERS, icon: Orders }]
      : []),
    { title: t("My_Business.Menu"), id: MyBusinessContentItems.MY_MENU, icon: MenuList },
    { title: t("My_Business.Profile"), id: MyBusinessContentItems.PROFILE, icon: Building },
    { title: t("My_Business.Services"), id: MyBusinessContentItems.SERVICES, icon: Service },
    { title: t("My_Business.Finance"), id: MyBusinessContentItems.FINANCE, icon: Dollar },
    { title: t("My_Business.QR_Code"), id: MyBusinessContentItems.QR, icon: QrCode },
  ];

  const PAGE_CONTENTS = {
    [MyBusinessContentItems.PROFILE]: (
      <BusinessProfile data={data} onUpdate={onCompanyUpdate} />
    ),
    [MyBusinessContentItems.MY_MENU]: (
      <MyMenu
        company_id={data.id!}
        business_type={data.type!}
        companyTitle={data.name!}
        currencySymbol={currencySymbol!}
        template={data.styles}
        onUpdate={onCompanyUpdate}
      />
    ),
    [MyBusinessContentItems.QR]: (
      <Qr qrImage={data.qr_menu!} company_id={data.id!} companyTitle={data.name!} />
    ),
    [MyBusinessContentItems.FINANCE]: <Finances companyId={data.id!} />,
    [MyBusinessContentItems.STAFF]: <Staff companyId={data.id!} />,
    [MyBusinessContentItems.SERVICES]: <Services data={data} onUpdate={onCompanyUpdate} />,
    [MyBusinessContentItems.ORDERS]: (
      <OrdersPage
        orders={orders}
        company_id={data.id}
        QR={data.qr_menu}
        currencySymbol={currencySymbol}
        audioUnlocked={audioUnlocked}
        notificationSoundRef={notificationSoundRef}
      />
    ),
  };

  return {
    open,
    navRef,
    navHeight,
    largeScreen,
    navItemRefs,
    PAGE_CONTENTS,
    activeContent,
    newOrdersCount,
    dialogComponent,
    BUSINESS_PAGE_NAVS,
    isAnyServiceEnabled,
    notificationSoundRef,
    onCloseDialog,
    handleNavClick,
    setActiveContent,
  };
};

export default useMainPageHooks;
